import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  Briefcase, 
  FileText, 
  Send, 
  Calendar, 
  CheckSquare,
  Users,
  Clock,
  ClipboardList,
  Building2,
  Inbox,
  HardHat,
  FolderOpen,
  StickyNote,
  Wrench
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import PanelHeader from '../../components/PanelHeader';
import AlarmNotifications from '../../components/AlarmNotifications';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkPanel = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // State for attendance task
  const [hasAttendanceTask, setHasAttendanceTask] = useState(false);
  
  // State for notification counts (tüm kategoriler)
  const [notificationCounts, setNotificationCounts] = useState({
    isler: 0,
    gorevler: 0,
    taleplerim: 0,
    iletisim: 0,
    raporlar: 0,
    is_programi: 0,
    takvim: 0,
    sube_bilgileri: 0,
    total: 0
  });
  
  // Badges counts (seen-based system)
  const [badgeCounts, setBadgeCounts] = useState({
    jobs: { total: 0 },
    tasks: { total: 0 },
    communication: { total: 0 },
    reports: { total: 0 },
    assignments: { total: 0 },
    calendar: { total: 0 },
    sube_bilgileri: { total: 0 }
  });
  
  // İşler menüsü için bildirim sayısı
  const [jobsNotificationCount, setJobsNotificationCount] = useState(0);
  
  // YENİ badge sistemi - görülmemiş iş ve talep sayıları
  const [unseenJobsCount, setUnseenJobsCount] = useState(0);
  const [unseenRequestsCount, setUnseenRequestsCount] = useState(0);
  
  // Okunmamış mesaj sayısı
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  
  // Okunmamış rapor sayısı
  const [unreadReportsCount, setUnreadReportsCount] = useState(0);
  
  // Taşeron yeni kayıt sayısı
  const [subcontractorNewCount, setSubcontractorNewCount] = useState(0);
  
  // Dosyalar yeni belge sayısı
  const [newFilesCount, setNewFilesCount] = useState(0);
  
  // Şube Duvarı yeni paylaşım/yorum sayısı
  const [newWallCount, setNewWallCount] = useState(0);
  
  // Alınan Hizmetler yeni/güncellenen sayısı
  const [newServicesCount, setNewServicesCount] = useState(0);
  
  // Bilgi Kartları yeni sayısı
  const [newInfoCardsCount, setNewInfoCardsCount] = useState(0);
  
  // İş Programı yeni sayısı
  const [newSchedulesCount, setNewSchedulesCount] = useState(0);

  // Check if user has manager access (branch or hr panel)
  const panelAccess = user?.panel_access || [];
  const isManager = panelAccess.includes('branch') || panelAccess.includes('hr') || panelAccess.includes('ik_paneli') || user?.role === 'admin' || user?.role === 'hr_manager';

  // Fetch notification counts
  const fetchNotificationCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
      if (!token) return;
      
      const res = await axios.get(`${API}/notifications/counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationCounts(res.data || {});
    } catch (error) {
      console.error('Bildirim sayıları alınamadı:', error);
    }
  }, []);

  // İşler menüsü için bildirim sayısını getir
  const fetchJobsNotificationCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
      if (!token) return;
      
      const res = await axios.get(`${API}/work/jobs/notification-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobsNotificationCount(res.data.count || 0);
    } catch (error) {
      console.error('İşler bildirim sayısı alınamadı:', error);
    }
  }, []);

  // Görülmemiş iş sayısını getir (YENİ badge sistemi)
  const fetchUnseenJobsCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
      if (!token) return;
      
      const res = await axios.get(`${API}/work/jobs/unseen-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnseenJobsCount(res.data.unseen_count || 0);
    } catch (error) {
      console.error('Görülmemiş iş sayısı alınamadı:', error);
    }
  }, []);

  // Görülmemiş talep sayısını getir (YENİ badge sistemi)
  const fetchUnseenRequestsCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
      if (!token) return;
      
      const res = await axios.get(`${API}/work/requests/my/unseen-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnseenRequestsCount(res.data.unseen_count || 0);
    } catch (error) {
      console.error('Görülmemiş talep sayısı alınamadı:', error);
    }
  }, []);

  // Okunmamış mesaj sayısını getir
  const fetchUnreadMessagesCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
      if (!token) return;
      
      const res = await axios.get(`${API}/notifications/unread-counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadMessagesCount(res.data.messages || 0);
      setUnreadReportsCount(res.data.reports || 0);
    } catch (error) {
      console.error('Okunmamış sayılar alınamadı:', error);
    }
  }, []);

  // Badge counts (seen-based) - şubedeki yeni öğeler
  const fetchBadgeCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
      if (!token) return;
      
      const res = await axios.get(`${API}/badges/counts?panel=work`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBadgeCounts(res.data || {});
    } catch (error) {
      console.error('Badge sayıları alınamadı:', error);
    }
  }, []);

  // Mark notifications as read for a category
  const markCategoryAsRead = useCallback(async (category) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
      if (!token) return;
      
      await axios.put(`${API}/notifications/read-all?category=${category}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh counts after marking as read
      fetchNotificationCounts();
    } catch (error) {
      console.error('Bildirimler okundu işaretlenemedi:', error);
    }
  }, [fetchNotificationCounts]);

  // Mark badge as seen
  const markBadgeSeen = useCallback(async (menu) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
      if (!token) return;
      
      await axios.post(`${API}/badges/mark-seen`, {
        panel: 'work',
        menu: menu,
        tab: '',
        sub_tab: ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh badge counts
      fetchBadgeCounts();
    } catch (error) {
      console.error('Badge seen işaretlenemedi:', error);
    }
  }, [fetchBadgeCounts]);

  // Taşeron yeni kayıt sayısını çek
  const fetchSubcontractorNewCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
      if (!token) return;
      const response = await axios.get(`${API}/work/subcontractors/new-counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubcontractorNewCount(response.data?.total_new || 0);
    } catch (error) {
      console.error('Taşeron yeni sayısı alınamadı:', error);
    }
  }, []);

  // Dosyalar yeni belge sayısını çek
  const fetchNewFilesCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
      if (!token) return;
      const response = await axios.get(`${API}/work/files/new-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewFilesCount(response.data?.new_count || 0);
    } catch (error) {
      console.error('Dosyalar yeni belge sayısı alınamadı:', error);
    }
  }, []);

  // Şube Duvarı yeni paylaşım/yorum sayısını çek
  const fetchNewWallCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
      if (!token) return;
      const response = await axios.get(`${API}/wall/new-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewWallCount(response.data?.total_new || 0);
    } catch (error) {
      console.error('Şube Duvarı yeni sayısı alınamadı:', error);
    }
  }, []);

  // Alınan Hizmetler yeni/güncellenen sayısını çek
  const fetchNewServicesCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
      if (!token) return;
      const response = await axios.get(`${API}/work/services/new-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewServicesCount(response.data?.total_new || 0);
    } catch (error) {
      console.error('Alınan Hizmetler yeni sayısı alınamadı:', error);
    }
  }, []);

  // Bilgi Kartları yeni sayısını çek
  const fetchNewInfoCardsCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
      if (!token) return;
      const response = await axios.get(`${API}/my/branch-info-cards/new-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewInfoCardsCount(response.data?.new_count || 0);
    } catch (error) {
      console.error('Bilgi Kartları yeni sayısı alınamadı:', error);
    }
  }, []);

  // İş Programı yeni sayısını çek
  const fetchNewSchedulesCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
      if (!token) return;
      const response = await axios.get(`${API}/work/schedules/new-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewSchedulesCount(response.data?.total || 0);
    } catch (error) {
      console.error('İş Programı yeni sayısı alınamadı:', error);
    }
  }, []);

  // Check for attendance task on mount
  useEffect(() => {
    checkAttendanceTask();
    fetchNotificationCounts();
    fetchJobsNotificationCount();
    fetchBadgeCounts();
    fetchUnseenJobsCount();
    fetchUnseenRequestsCount();
    fetchUnreadMessagesCount();
    fetchSubcontractorNewCount();
    fetchNewFilesCount();
    fetchNewWallCount();
    fetchNewServicesCount();
    fetchNewInfoCardsCount();
    fetchNewSchedulesCount();
    
    // Refresh counts every 5 seconds for real-time updates
    const interval = setInterval(() => {
      fetchNotificationCounts();
      fetchJobsNotificationCount();
      fetchBadgeCounts();
      fetchUnseenJobsCount();
      fetchUnseenRequestsCount();
      fetchUnreadMessagesCount();
      fetchSubcontractorNewCount();
      fetchNewFilesCount();
      fetchNewWallCount();
      fetchNewServicesCount();
      fetchNewInfoCardsCount();
      fetchNewSchedulesCount();
      checkAttendanceTask();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchNotificationCounts, fetchJobsNotificationCount, fetchBadgeCounts, fetchUnseenJobsCount, fetchUnseenRequestsCount, fetchUnreadMessagesCount, fetchSubcontractorNewCount, fetchNewFilesCount, fetchNewWallCount, fetchNewServicesCount, fetchNewInfoCardsCount, fetchNewSchedulesCount]);

  // Mark category as read when navigating to a section
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/work/jobs')) {
      markCategoryAsRead('isler');
      markBadgeSeen('jobs');
    } else if (path.includes('/work/tasks')) {
      markCategoryAsRead('gorevler');
      markBadgeSeen('tasks');
    } else if (path.includes('/work/requests')) {
      markCategoryAsRead('taleplerim');
    } else if (path.includes('/work/communication')) {
      markCategoryAsRead('iletisim');
      markBadgeSeen('communication');
    } else if (path.includes('/work/reports')) {
      markCategoryAsRead('raporlar');
      markBadgeSeen('reports');
    } else if (path.includes('/work/assignments')) {
      markCategoryAsRead('is_programi');
      markBadgeSeen('assignments');
    } else if (path.includes('/work/calendar')) {
      markCategoryAsRead('takvim');
      markBadgeSeen('calendar');
    } else if (path.includes('/work/sube-bilgileri')) {
      markCategoryAsRead('sube_bilgileri');
      markBadgeSeen('sube_bilgileri');
    }
    // Refresh counts on any navigation
    fetchNotificationCounts();
    fetchJobsNotificationCount();
    fetchBadgeCounts();
  }, [location.pathname, markCategoryAsRead, markBadgeSeen, fetchNotificationCounts, fetchJobsNotificationCount, fetchBadgeCounts]);

  // İşler sayfasından çıkıldığında unseen count'u yenile (işler görüldü olarak işaretlenmiş olabilir)
  useEffect(() => {
    // Sayfa değiştiğinde unseen count'u yenile
    const timer = setTimeout(() => {
      fetchUnseenJobsCount();
      fetchUnseenRequestsCount();
    }, 500); // 500ms bekle - mark-seen API'nin tamamlanması için
    
    return () => clearTimeout(timer);
  }, [location.pathname, fetchUnseenJobsCount, fetchUnseenRequestsCount]);

  // Custom event listener - WorkJobs.js'den mark-seen yapıldığında sidebar'ı güncelle
  useEffect(() => {
    const handleRefreshUnseenCount = () => {
      fetchUnseenJobsCount();
      fetchUnseenRequestsCount();
    };
    
    window.addEventListener('refreshUnseenCount', handleRefreshUnseenCount);
    return () => window.removeEventListener('refreshUnseenCount', handleRefreshUnseenCount);
  }, [fetchUnseenJobsCount, fetchUnseenRequestsCount]);

  // Custom event listener - ShubeBilgileri.js'den mark-all-seen yapıldığında badge'i güncelle
  useEffect(() => {
    const handleRefreshInfoCards = () => {
      fetchNewInfoCardsCount();
    };
    window.addEventListener('refreshInfoCardsCount', handleRefreshInfoCards);
    return () => window.removeEventListener('refreshInfoCardsCount', handleRefreshInfoCards);
  }, [fetchNewInfoCardsCount]);

  const checkAttendanceTask = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
      if (!token) return;
      
      // Önce yeni taşeron bazlı sistemi kontrol et
      try {
        const newRes = await axios.get(`${API}/my/subcontractor-attendance-tasks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (newRes.data.has_task) {
          setHasAttendanceTask(true);
          return;
        }
      } catch (newErr) {
        // Yeni endpoint yoksa devam et
      }
      
      // Eski sistemi kontrol et (geriye uyumluluk)
      const res = await axios.get(`${API}/my/attendance-task`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasAttendanceTask(res.data.has_task);
    } catch (error) {
      console.error('Görev kontrolünde hata:', error);
      setHasAttendanceTask(false);
    }
  };

  // Helper: Get badge count from badge system only (not old notifications)
  const getBadgeFromSystem = (badgeKey) => {
    return badgeCounts[badgeKey]?.total || 0;
  };

  // Toplam görülmemiş sayısı (işler + talepler - reddedilen talepler dahil)
  const totalUnseenCount = unseenJobsCount + unseenRequestsCount;

  const menuItems = [
    { path: '/work/jobs', icon: Briefcase, label: 'İŞLER', badge: totalUnseenCount > 0 ? totalUnseenCount : null, category: 'isler', isNewBadge: totalUnseenCount > 0, isUnseen: totalUnseenCount > 0, menuKey: 'jobs' },
    { path: '/work/communication', icon: Inbox, label: 'İLETİŞİM', badge: unreadMessagesCount > 0 ? unreadMessagesCount : null, category: 'iletisim', isNewBadge: unreadMessagesCount > 0, menuKey: 'communication' },
    { path: '/work/reports', icon: FileText, label: 'RAPORLAR', badge: unreadReportsCount > 0 ? unreadReportsCount : null, category: 'raporlar', isNewBadge: unreadReportsCount > 0, menuKey: 'reports' },
    { path: '/work/assignments', icon: Clock, label: 'İŞ PROGRAMI', badge: newSchedulesCount > 0 ? newSchedulesCount : null, category: 'is_programi', isNewBadge: newSchedulesCount > 0, menuKey: 'assignments' },
    { path: '/work/calendar', icon: Calendar, label: 'TAKVİM', badge: getBadgeFromSystem('calendar') || null, category: 'takvim', isNewBadge: getBadgeFromSystem('calendar') > 0, menuKey: 'calendar' },
    { path: '/work/subcontractors', icon: HardHat, label: 'TAŞERONLAR', badge: subcontractorNewCount > 0 ? subcontractorNewCount : null, category: 'taseronlar', isNewBadge: subcontractorNewCount > 0, menuKey: 'subcontractors' },
    { path: '/work/services', icon: Wrench, label: 'ALINAN HİZMETLER', badge: newServicesCount > 0 ? newServicesCount : null, category: 'services', menuKey: 'services', isNewBadge: newServicesCount > 0, isUnseen: newServicesCount > 0 },
    // Manager-only menu item
    ...(isManager ? [{ path: '/work/manager-attendance', icon: Users, label: 'MESAİ TAKİBİ', badge: null, category: null }] : []),
    // Branch info - available to all employees
    { path: '/work/sube-bilgileri', icon: Building2, label: 'ŞUBE BİLGİLERİ', badge: newInfoCardsCount > 0 ? newInfoCardsCount : null, category: 'sube_bilgileri', isNewBadge: newInfoCardsCount > 0, menuKey: 'branch_info' },
    { path: '/work/files', icon: FolderOpen, label: 'DOSYALAR', badge: newFilesCount > 0 ? newFilesCount : null, category: null, menuKey: 'files', isNewBadge: newFilesCount > 0, isUnseen: newFilesCount > 0 },
    { path: '/work/wall', icon: StickyNote, label: 'ŞUBE DUVARI', badge: newWallCount > 0 ? newWallCount : null, category: null, menuKey: 'wall', isNewBadge: newWallCount > 0, isUnseen: newWallCount > 0 },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="work-panel">
      {/* Header */}
      <PanelHeader
        title="İş Paneli"
        titleIcon={Briefcase}
        titleColor="text-blue-600"
      />

      {/* Add pt-16 to account for fixed header height (h-16 = 4rem) */}
      <div className="flex pt-16">
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-4rem)] border-r border-[hsl(var(--border))] bg-white p-4">
          <nav className="space-y-2">
            {/* Conditional Taşeron Mesai Takibi button for assigned employees */}
            {hasAttendanceTask && (
              <>
                <Link
                  to="/dashboard/mesai-takip"
                  className="flex items-center gap-3 px-4 py-3 rounded-md transition-colors bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100"
                  data-testid="menu-taseron-mesai-takibi"
                >
                  <ClipboardList className="h-5 w-5" />
                  <span className="font-medium">TAŞERON MESAİ TAKİBİ</span>
                </Link>
                <div className="border-b my-2" />
              </>
            )}
            
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                    isActive(item.path)
                      ? 'bg-blue-600 text-white'
                      : 'hover:bg-blue-50 text-gray-700'
                  }`}
                  data-testid={`menu-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                  {item.badge && (
                    <Badge className={`ml-auto ${
                      item.isNewBadge 
                        ? 'bg-red-500 text-white animate-pulse' 
                        : isActive(item.path) 
                          ? 'bg-white text-blue-600' 
                          : 'bg-blue-100 text-blue-700'
                    }`}>
                      {item.isNewBadge ? `${item.badge} yeni` : item.badge}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default WorkPanel;
